import { provideTaiga, TUI_DARK_MODE } from '@taiga-ui/core';
import { tuiInputPhoneOptionsProvider } from '@taiga-ui/kit';
import {
  provideClientHydration,
  withEventReplay,
  withIncrementalHydration,
} from '@angular/platform-browser';
import {
  ApplicationConfig,
  provideZoneChangeDetection,
  provideAppInitializer,
  inject,
  signal,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { TUI_LANGUAGE, TUI_RUSSIAN_LANGUAGE } from '@taiga-ui/i18n';
import { AbstractSecurityStorage, LogLevel, provideAuth } from 'angular-auth-oidc-client';
import { routes } from './app.routes';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { BreadcrumbTrailService } from './core/services/breadcrumb-trail.service';
import { environment } from '../environments/environment';
import { OidcLocalStorageService } from './core/services/oidc-local-storage.service';
import { resolveOidcBrowserRouting } from './core/services/oidc-browser-routing';

export function createAppConfig(): ApplicationConfig {
  const publicUrl = environment.publicUrl || 'https://runtime.invalid';
  const oidcRouting = resolveOidcBrowserRouting(
    publicUrl,
    environment.oidcAuthUrl,
    environment.oidcBrowserProxyEnabled,
    environment.oidcBrowserProxyBasePath,
    Date.now().toString(36),
  );

  return {
    providers: [
      // Активируем построение хлебных крошек на старте, чтобы ловить все переходы.
      provideAppInitializer(() => {
        inject(BreadcrumbTrailService);
      }),
      {
        provide: HTTP_INTERCEPTORS,
        useClass: AuthInterceptor,
        multi: true,
      },
      provideClientHydration(withEventReplay(), withIncrementalHydration()),
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideRouter(routes),
      provideHttpClient(withInterceptorsFromDi(), withFetch()),
      {
        provide: TUI_LANGUAGE,
        useValue: signal(TUI_RUSSIAN_LANGUAGE),
      },
      provideAuth({
        config: {
          authority: `${environment.oidcAuthUrl}`,
          redirectUrl: environment.oidcRedirectUrl,
          postLogoutRedirectUri: environment.oidcPostLogoutRedirectUri,
          clientId: environment.oidcClientId,
          scope: environment.oidcAuthScope,
          responseType: environment.oidcResponseType,
          authWellknownEndpointUrl: oidcRouting.wellKnownEndpointUrl,
          authWellknownEndpoints: oidcRouting.wellKnownEndpoints,
          strictIssuerValidationOnWellKnownRetrievalOff: oidcRouting.strictIssuerValidationOff,
          silentRenew: true,
          useRefreshToken: true,

          triggerAuthorizationResultEvent: true,

          renewTimeBeforeTokenExpiresInSeconds: 30,
          logLevel: LogLevel.Error,
          secureRoutes: [`${environment.ordersV2Url}/`, `${environment.financeV2Url}/`],
          customParamsAuthRequest: {
            display: 'popup',
            prompt: 'select_account',
          },
        },
      }),
      // По умолчанию angular-auth-oidc-client кладёт токены (в т.ч. refresh) в
      // sessionStorage — она стирается при закрытии вкладки/браузера и не общая
      // между вкладками, из-за чего приходится логиниться заново. localStorage
      // переживает перезагрузку и перезапуск браузера, сессия восстанавливается
      // по refresh-токену. SSR безопасен: BrowserStorageService не лезет в storage
      // на сервере (проверяет hasStorage()).
      { provide: AbstractSecurityStorage, useClass: OidcLocalStorageService },
      provideTaiga(),
      { provide: TUI_DARK_MODE, useValue: signal(false) },
      tuiInputPhoneOptionsProvider({ mask: '+7 ### ###-##-##' }),
    ],
  };
}
